import * as React from 'react';

import { SortableContainer } from 'react-sortable-hoc';

import SortableCard from './SortableCard';
import Loader from '../../common/Loader/Loader';
import GreetingCard from './questionType/greetingText/GreetingCard';
import DefaultSortableGroupCard from './SortableGroupCard';
import {
    QTYPES,
    Project,
    QuestionSummary,
    Question,
    QuestionID,
    SPECIAL_QIDS
} from '@nexxt/common/types';
import RedirectCard from './questionType/RedirectText/RedirectCard';
import { PROJECT_EDIT_MODES } from '../../../interface/project.interface';
import { dataTestid } from '../../../constants/dataTestid';

export interface IQuestionListView {
    search: string;
    onAdd: (addPosition?: number, parent?: QuestionID) => void;
    onSearchChange: (event?: any) => void;
    moveCard: (
        dragIndex: number,
        hoverIndex: number,
        collection: number
    ) => void;
    dragStop: () => void;
    isLoading: boolean;
    questionSummaries: QuestionSummary[];
    selectedQuestion: Question;
    onSelectQuestion: (
        questionSummary: QuestionSummary,
        forceParent?: boolean
    ) => void;
    currentProject: Project;
    editMode: PROJECT_EDIT_MODES;
    editEnabled: boolean;
    projectHealth: any;
    actions: {
        edit: (question: any, forceParent?: boolean) => void;
        preview: (question: any) => void;
        delete: (question: any) => void;
        logic: (question: any) => void;
        duplicate: (question: any) => void;
    };
    initialSelectedQuestionID: number;
    showError: boolean;
}

const QuestionListView: React.SFC<IQuestionListView> = (props) => {
    const [activeMenuID, setActiveMenuID] = React.useState(-1);
    const toggleActiveMenuID = (id: number) => {
        if (activeMenuID === id) {
            setActiveMenuID(-1);
            return;
        }
        setActiveMenuID(id);
    };
    const renderQuestionCards = () => {
        return props.questionSummaries.map(
            (questionSummary: QuestionSummary | any, index) => {
                const isAudience = questionSummary.isAudience;
                if (
                    questionSummary.type === QTYPES.GROUP ||
                    questionSummary.type === QTYPES.POPUP
                ) {
                    return (
                        <DefaultSortableGroupCard
                            key={questionSummary.id}
                            index={index}
                            activeMenuID={activeMenuID}
                            toggleActiveMenuID={toggleActiveMenuID}
                            questionSummary={questionSummary}
                            position={questionSummary.position}
                            moveCard={props.moveCard}
                            dragStop={props.dragStop}
                            onSelectQuestion={props.onSelectQuestion}
                            actions={props.actions}
                            languages={props.currentProject.languages}
                            selectedQuestionId={
                                props.selectedQuestion &&
                                props.selectedQuestion.id
                            }
                            editEnabled={props.editEnabled}
                            selected={false} // not in use
                            projectHealth={props.projectHealth}
                            health={
                                props.projectHealth &&
                                props.projectHealth[questionSummary.id]
                            }
                            onAdd={props.onAdd}
                            currentProject={props.currentProject}
                            editMode={props.editMode}
                            initialSelectedQuestionID={
                                props.initialSelectedQuestionID
                            }
                            showError={props.showError}
                        />
                    );
                }
                return (
                    <SortableCard
                        key={questionSummary.id}
                        index={index}
                        activeMenuID={activeMenuID}
                        toggleActiveMenuID={toggleActiveMenuID}
                        questionSummary={questionSummary}
                        position={questionSummary.position}
                        moveCard={props.moveCard}
                        dragStop={props.dragStop}
                        onSelectQuestion={props.onSelectQuestion}
                        actions={props.actions}
                        languages={props.currentProject.languages || []}
                        selected={
                            props.selectedQuestion &&
                            props.selectedQuestion.id === questionSummary.id
                        }
                        disabled={false}
                        editEnabled={props.editEnabled}
                        health={
                            props.projectHealth &&
                            props.projectHealth[questionSummary.id]
                        }
                        collection={0}
                        onAdd={props.onAdd}
                        currentProject={props.currentProject}
                        editMode={props.editMode}
                        initialSelectedQuestionID={
                            props.initialSelectedQuestionID
                        }
                        showError={props.showError}
                    />
                );
            }
        );
    };

    // }
    return (
        <div className="project-add--question-list">
            <div className="question-list--filters">
                <div className="filters--search qlist">
                    <input
                        type="text"
                        placeholder="Search questions by label"
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                        ) => props.onSearchChange(event.target.value)}
                        value={props.search}
                    />
                    <i className="nx nx-search" />
                </div>
            </div>
            <div className="question-list--body">
                <div className="question-cards">
                    {props.isLoading ? (
                        <Loader />
                    ) : (
                        <>
                            <GreetingCard
                                actions={props.actions}
                                onSelectQuestion={props.onSelectQuestion}
                                search={props.search}
                                disabled={!props.editEnabled}
                                health={
                                    props.projectHealth &&
                                    props.projectHealth[
                                        SPECIAL_QIDS.GREETING_TEXT
                                    ]
                                }
                            />
                            {renderQuestionCards()}
                            <RedirectCard
                                actions={props.actions}
                                onSelectQuestion={props.onSelectQuestion}
                                search={props.search}
                                disabled={!props.editEnabled}
                                initialSelectedQuestionID={
                                    props.initialSelectedQuestionID
                                }
                                health={
                                    props.projectHealth &&
                                    props.projectHealth[
                                        SPECIAL_QIDS.REDIRECT_TEXT
                                    ]
                                }
                            />
                        </>
                    )}
                </div>
            </div>
            <div className="question-list--footer">
                {!props.isLoading ? (
                    <button
                        className="btn btn--md btn--green"
                        data-test={dataTestid.NEW_QUESTION_BTN}
                        onClick={() => props.onAdd()}
                        disabled={!props.editEnabled}
                    >
                        <i className="nx nx-add" />
                        Add new question
                    </button>
                ) : null}
            </div>
        </div>
    );
};

const SortableOptionContainer = SortableContainer(QuestionListView);

class WrapperClass extends React.Component<IQuestionListView, any> {
    onSortEnd = ({ oldIndex, newIndex, collection }) => {
        if (oldIndex !== newIndex) {
            console.log(
                'moved',
                oldIndex,
                newIndex,
                'to collection ',
                collection
            );
            this.props.moveCard(oldIndex, newIndex, collection);
        }
    };
    render() {
        return (
            <SortableOptionContainer
                {...this.props}
                onSortEnd={this.onSortEnd}
                useDragHandle={true}
                pressDelay={50}
            />
        );
    }
}

export default WrapperClass;
